import React, { useState, useRef, useEffect, useCallback } from 'react';

const ReelCommentSheet = ({ reel, currentUser, onClose, onAddComment }) => {
  const [input, setInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || !currentUser) return;
    setSubmitting(true);
    try {
      await onAddComment(input.trim());
      setInput('');
      setTimeout(() => {
        listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } finally {
      setSubmitting(false);
    }
  };

  const comments = reel?.comments || [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[201] rounded-t-2xl overflow-hidden"
        style={{
          background: 'rgba(15,15,20,0.97)',
          backdropFilter: 'blur(40px)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          maxHeight: '75vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <h3 className="font-bold text-white text-base">
            {reel?.commentsCount ?? comments.length} Comments
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <span className="material-symbols-outlined text-white/60 text-[18px]">close</span>
          </button>
        </div>

        {/* Comment list */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto px-5 py-4 space-y-5"
          style={{ minHeight: 0 }}
        >
          {comments.length === 0 ? (
            <div className="text-center py-10">
              <span className="material-symbols-outlined text-white/20 text-[40px] block mb-2">chat_bubble_outline</span>
              <p className="text-white/40 text-sm">No comments yet. Be the first!</p>
            </div>
          ) : (
            comments.map((c, i) => (
              <div key={c.id || i} className="flex gap-3">
                <img
                  src={c.author?.avatarUrl || 'https://images.unsplash.com/photo-1611695434398-4f4b330623e6'}
                  alt={c.author?.fullName}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-white font-semibold text-sm">{c.author?.username || 'user'}</span>
                    <span className="text-white/30 text-[10px]">
                      {new Date(c.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-white/80 text-sm mt-0.5 leading-relaxed">{c.content}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={handleSubmit}
          className="px-4 py-3 flex gap-3 items-center"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          {currentUser && (
            <img
              src={currentUser.avatar || 'https://images.unsplash.com/photo-1611695434398-4f4b330623e6'}
              alt="you"
              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
            />
          )}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={currentUser ? 'Add a comment…' : 'Log in to comment'}
            disabled={!currentUser}
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-white/30 transition-colors"
            style={{ minWidth: 0 }}
          />
          <button
            type="submit"
            disabled={!input.trim() || submitting || !currentUser}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all disabled:opacity-30"
            style={{
              background: input.trim() && currentUser
                ? 'linear-gradient(135deg,#c9a96e,#e8c98a)'
                : 'rgba(255,255,255,0.1)',
            }}
          >
            <span className="material-symbols-outlined text-black text-[18px]">send</span>
          </button>
        </form>
      </div>
    </>
  );
};

export default ReelCommentSheet;
